'use client';

import { usePathname, useRouter } from 'next/navigation';

import type { TypesenseEventDocument } from '@/types';
import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui';
import { IconCalendarPlus, IconCopy, IconDots, IconInfoCircle, IconShare } from '@tabler/icons-react';
import { toast } from 'sonner';
import { useCopyToClipboard } from 'usehooks-ts';

import { usePlacePrefix } from '@/lib/context/venues-page-context';
import { useGlobal } from '@/lib/global-store';

export function EventActions({ event, className }: { event: TypesenseEventDocument; className?: string }) {
  const [, copy] = useCopyToClipboard();
  const { setOpenBook } = useGlobal();
  const router = useRouter();
  const pathname = usePathname();
  const prefix = usePlacePrefix();

  const date = new Date(event.dates * 1000);
  const endDate = event.dates_end ? new Date(event.dates_end * 1000) : undefined;
  const eventName = event.eventName;
  const eventLink = `${prefix}/events/${event.slug}?date=${date.toISOString()}`;
  const fullUrl = typeof window !== 'undefined' ? `${window.location.origin}${eventLink}` : eventLink;

  const handleAddToCalendar = () => {
    const title = eventName;
    const start = date.toISOString();
    const end = endDate ? endDate.toISOString() : date.toISOString();
    const description = event.description ? event.description.replace(/<[^>]*>/g, '') : '';

    // Create ICS file content
    const icsContent = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      `SUMMARY:${title}`,
      `DTSTART:${start.replace(/[-:]/g, '').replace(/\.\d+/g, '')}`,
      `DTEND:${end.replace(/[-:]/g, '').replace(/\.\d+/g, '')}`,
      `DESCRIPTION:${description.replace(/\n/g, '\\n')}`,
      `URL:${fullUrl}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');

    // Create a Blob and generate download link
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const href = URL.createObjectURL(blob);

    // Create a link and trigger download
    const link = document.createElement('a');
    link.href = href;
    link.setAttribute('download', `${event.slug || 'event'}.ics`);
    document.body.appendChild(link);
    link.click();

    // Clean up
    document.body.removeChild(link);
    URL.revokeObjectURL(href);

    toast.success('Calendar event downloaded');
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: eventName,
          url: fullUrl,
        });
      } catch (error) {
        // Check if the error is just the user cancelling the share dialog
        const isUserCancelled =
          error instanceof Error && (error.name === 'AbortError' || /cancelled|canceled|aborted/i.test(error.message));

        if (!isUserCancelled) {
          console.error('Error sharing:', error);
          toast.error('Failed to share');
        }
      }
    } else {
      // Fallback for browsers that don't support the Web Share API
      handleCopyLink();
      toast.success('Link copied to clipboard');
    }
  };

  const handleCopyLink = () => {
    copy(fullUrl)
      .then(() => {
        toast.success('Link copied to clipboard');
      })
      .catch((err) => {
        console.error('Failed to copy link:', err);
        toast.error('Failed to copy link');
      });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="smIcon" className={className}>
          <IconDots className="size-4 shrink-0" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        {!pathname.includes(event.slug) && (
          <DropdownMenuItem
            className="group"
            onClick={() => {
              setOpenBook(false);
              router.push(eventLink);
            }}
          >
            <IconInfoCircle className="text-muted-foreground group-hover:text-accent-foreground size-4" />
            More info
          </DropdownMenuItem>
        )}

        <DropdownMenuItem className="group" onClick={handleAddToCalendar}>
          <IconCalendarPlus className="text-muted-foreground group-hover:text-accent-foreground size-4" />
          Add to Calendar
        </DropdownMenuItem>

        <DropdownMenuItem className="group" onClick={handleShare}>
          <IconShare className="text-muted-foreground group-hover:text-accent-foreground size-4" />
          Share
        </DropdownMenuItem>

        <DropdownMenuItem className="group" onClick={handleCopyLink}>
          <IconCopy className="text-muted-foreground group-hover:text-accent-foreground size-4" />
          Copy Link
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
